/* ==========================================================================
   bj-cart-upsell.js — behaviour for the cart upsell rail
   (snippets/cart-upsell.liquid + snippets/bj-cart-upsell-card.liquid)

   WHY EVERYTHING IS DELEGATED FROM `document`
   Shopify.theme.ajaxCart.updateView() replaces the innerHTML of every
   `.js-ajax-cart-content` on the page after each cart change. Any listener
   bound directly to a card or swatch would be destroyed the first time
   someone changes their cart. Binding once at document level survives every
   re-render. For the same reason this cannot live in an inline <script>
   inside the cart markup: scripts inserted via innerHTML never execute.

   No dependencies, no polyfills, nothing imported.
   ========================================================================== */

(function () {
  'use strict';

  // Guard against a double include binding every delegated handler twice
  // (which would add two of everything to the cart).
  if (window.__bjCartUpsellInit) return;
  window.__bjCartUpsellInit = true;

  var TRACK = '[data-bju-track]';
  var ADDED_LABEL = 'Added ✓';
  var MIN_THUMB = 26;
  var STORE_KEY = 'bjuAddedFromRail';
  var scrollMemo = 0;

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }

  /* --- which products did THIS shopper add from the rail? ------------------
     Anything already in the bag is hidden by the Liquid, because re-recommending
     it is noise. But a product added from the rail has to stay put and show
     "Added", otherwise the card vanishes the instant it is tapped and the action
     looks like it failed. Liquid cannot tell those two cases apart — both are
     just "in the cart" — so the distinction is kept here.

     sessionStorage rather than a plain variable so the state survives navigating
     to another page and reopening the cart; the in-memory Set is what actually
     gets read, so a browser that refuses storage still works within the page. */
  var addedFromRail = (function () {
    try {
      return new Set(JSON.parse(sessionStorage.getItem(STORE_KEY) || '[]'));
    } catch (e) {
      return new Set();
    }
  })();

  function rememberAdd(pid) {
    if (!pid) return;
    addedFromRail.add(String(pid));
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(Array.from(addedFromRail)));
    } catch (e) {
      /* private mode / storage disabled — the in-memory Set still covers this page */
    }
  }

  /* --- attribution: which products came from the rail? ---------------------
     Writes a cart attribute so upsell-driven revenue can be counted in the
     Shopify admin (order page, and exports).

     The key is prefixed with an underscore, which is Shopify's convention for a
     HIDDEN attribute: it is not shown to the shopper in checkout or on the order
     status page, but it is there on the order in admin.

     The value is rebuilt from the cart every time rather than appended to, which
     is what keeps removals correct: a product taken out of the cart drops out of
     the attribute automatically, with no separate remove handler to go stale.

     Cost control — this does nothing at all, not even a request, for the vast
     majority of shoppers who never touch the rail (the Set is empty). When it
     does run it is one GET, and a POST only if the value actually changed, so it
     can never loop with the cart re-render. */
  var ATTR_KEY = '_cart_upsell';
  var syncTimer = null;

  function writeUpsellAttribute() {
    if (!addedFromRail.size) return;

    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        var names = [];
        var stillHere = new Set();

        for (var i = 0; i < cart.items.length; i++) {
          var item = cart.items[i];
          var pid = String(item.product_id);
          if (!addedFromRail.has(pid)) continue;
          stillHere.add(pid);
          var name = item.product_title || item.title;
          if (names.indexOf(name) === -1) names.push(name);
        }

        // Forget products that are no longer in the cart, so a re-add from the
        // PDP later is not mistaken for an upsell.
        if (stillHere.size !== addedFromRail.size) {
          addedFromRail = stillHere;
          try {
            sessionStorage.setItem(STORE_KEY, JSON.stringify(Array.from(addedFromRail)));
          } catch (e) {
            /* storage unavailable — in-memory Set still correct for this page */
          }
        }

        var desired = names.join(', ');
        var current = (cart.attributes && cart.attributes[ATTR_KEY]) || '';
        if (desired === current) return; // never write unless it actually changed

        var attributes = {};
        attributes[ATTR_KEY] = desired; // '' removes the attribute
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: attributes })
        });
      })
      .catch(function () {
        /* Tracking must never break the cart. Swallow and carry on. */
      });
  }

  function syncUpsellAttribute() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(writeUpsellAttribute, 150);
  }

  function syncCards() {
    var rails = document.querySelectorAll('[data-bju]');
    for (var r = 0; r < rails.length; r++) {
      var cards = rails[r].querySelectorAll('[data-bju-card]');
      var visible = 0;
      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        if (card.hasAttribute('data-bju-in-basket')) {
          if (addedFromRail.has(card.getAttribute('data-bju-pid'))) {
            card.removeAttribute('hidden');
          } else {
            card.setAttribute('hidden', '');
          }
        }
        if (!card.hasAttribute('hidden')) visible++;
      }
      // nothing left worth recommending — drop the heading too
      if (visible === 0) rails[r].setAttribute('hidden', '');
      else rails[r].removeAttribute('hidden');
    }
  }

  function cartConfig() {
    try {
      return JSON.parse(document.getElementById('cart-config').innerHTML || '{}');
    } catch (e) {
      return {};
    }
  }

  /* --- scroll indicator ---------------------------------------------------
     A real scrollbar is no use here: mobile browsers only paint an overlay one
     while a scroll is in flight, so there is nothing to tell a shopper the rail
     moves. This is a persistent bar that mirrors position and proportion, and
     hides itself entirely when everything already fits. */
  function updateBar(track) {
    var root = closest(track, '[data-bju]');
    if (!root) return;
    var bar = root.querySelector('[data-bju-bar]');
    if (!bar) return;
    var thumb = bar.querySelector('[data-bju-bar-thumb]');
    if (!thumb) return;

    var max = track.scrollWidth - track.clientWidth;

    // clientWidth is 0 while the drawer popover is still display:none; the
    // ResizeObserver below re-runs this the moment it opens.
    if (track.clientWidth === 0 || max <= 2) {
      bar.setAttribute('hidden', '');
      return;
    }
    bar.removeAttribute('hidden');

    var barW = bar.clientWidth;
    if (!barW) return;

    var thumbW = Math.max(MIN_THUMB, Math.round(barW * (track.clientWidth / track.scrollWidth)));
    var travel = barW - thumbW;
    var pos = Math.round((track.scrollLeft / max) * travel);

    thumb.style.width = thumbW + 'px';
    thumb.style.transform = 'translateX(' + pos + 'px)';
  }

  var resizeObserver =
    typeof ResizeObserver === 'function'
      ? new ResizeObserver(function (entries) {
          for (var i = 0; i < entries.length; i++) updateBar(entries[i].target);
        })
      : null;

  function initRails() {
    // visibility first — it changes scrollWidth, which the bar is measured from
    syncCards();
    var tracks = document.querySelectorAll(TRACK);
    for (var i = 0; i < tracks.length; i++) {
      updateBar(tracks[i]);
      if (resizeObserver && tracks[i].getAttribute('data-bju-observed') !== '1') {
        tracks[i].setAttribute('data-bju-observed', '1');
        resizeObserver.observe(tracks[i]);
      }
    }
  }

  /* --- swatch selection --------------------------------------------------
     Colour changes price on this store (Twinkle £43 silver / £52 gold), so
     picking a swatch has to re-price the card, not just recolour it. */
  function selectSwatch(btn) {
    var card = closest(btn, '[data-bju-card]');
    if (!card) return;

    var group = closest(btn, '.bju__swatches');
    if (group) {
      var all = group.querySelectorAll('[data-bju-swatch]');
      for (var i = 0; i < all.length; i++) {
        var on = all[i] === btn;
        all[i].setAttribute('aria-checked', on ? 'true' : 'false');
        all[i].tabIndex = on ? 0 : -1;
      }
    }

    // innerHTML, not textContent: money_format on this shop emits
    // <span class=money>£46.00</span>, and textContent would print the tags.
    // The value comes from Liquid's money filter, not from user input.
    var priceEl = card.querySelector('[data-bju-price]');
    var priceFmt = btn.getAttribute('data-bju-price-fmt');
    if (priceEl && priceFmt) priceEl.innerHTML = priceFmt;

    var img = card.querySelector('[data-bju-img]');
    var newSrc = btn.getAttribute('data-bju-img');
    if (img && newSrc) img.src = newSrc;

    // Each colour tracks its own cart state, so switching from an added colour
    // to one that is not in the cart must re-enable the button.
    var soldOut = btn.getAttribute('data-bju-soldout') === 'true';
    var inCart = btn.getAttribute('data-bju-incart') === 'true';
    var add = card.querySelector('[data-bju-add]');
    if (add) {
      add.setAttribute('data-bju-vid', btn.getAttribute('data-bju-vid') || '');
      add.classList.remove('is-error');
      add.classList.toggle('is-added', inCart);
      add.disabled = soldOut || inCart;
      add.textContent = inCart
        ? ADDED_LABEL
        : soldOut
        ? 'Sold out'
        : add.getAttribute('data-bju-label') || 'Add';
    }
  }

  function moveSwatch(btn, dir) {
    var group = closest(btn, '.bju__swatches');
    if (!group) return;
    var all = Array.prototype.slice.call(group.querySelectorAll('[data-bju-swatch]'));
    var i = all.indexOf(btn);
    if (i < 0) return;
    var next = all[(i + dir + all.length) % all.length];
    selectSwatch(next);
    next.focus();
  }

  /* --- add to cart ------------------------------------------------------- */
  function refreshCart() {
    return fetch('/cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        // Rewrites every .js-ajax-cart-content — side cart AND cart page. The
        // re-render is also what turns the card's button into "Added", since
        // the Liquid checks the variant against the cart's variant ids.
        // showDrawer() is deliberately NOT called: in the drawer it is already
        // open, and on the cart page it would pop the drawer over the page.
        Shopify.theme.ajaxCart.updateView(cartConfig(), cart);
      });
  }

  function addToCart(btn) {
    var id = btn.getAttribute('data-bju-vid');
    if (!id || btn.disabled) return;

    var card = closest(btn, '[data-bju-card]');
    var pid = card && card.getAttribute('data-bju-pid');
    var label = btn.getAttribute('data-bju-label') || 'Add';
    btn.disabled = true;
    btn.classList.remove('is-error');
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) {
        if (!r.ok) {
          return r.json().then(function (j) {
            throw j;
          });
        }
        return r.json();
      })
      .then(function () {
        // Record BEFORE the refresh: the re-render will come back with this
        // card hidden as "in basket", and syncCards() needs to know to re-show it.
        rememberAdd(pid);
        syncUpsellAttribute();
        // Optimistic: if the re-render is slow the shopper still sees it land.
        if (document.body.contains(btn)) {
          btn.classList.add('is-added');
          btn.textContent = ADDED_LABEL;
        }
        return refreshCart().catch(function () {
          // Same last resort bj-pdp.js uses — the item IS in the cart, so send
          // them somewhere that shows it rather than leaving a dead button.
          window.location.href = '/cart';
        });
      })
      .catch(function () {
        // The add itself failed (sold out, limit reached, network).
        if (!document.body.contains(btn)) return;
        btn.disabled = false;
        btn.classList.remove('is-added');
        btn.classList.add('is-error');
        btn.textContent = 'Try again';
        setTimeout(function () {
          if (!document.body.contains(btn)) return;
          btn.classList.remove('is-error');
          btn.textContent = label;
        }, 2500);
      });
  }

  /* --- scroll: drive the indicator and remember the position ------------- */
  document.addEventListener(
    'scroll',
    function (e) {
      var t = e.target;
      if (t && t.nodeType === 1 && t.matches && t.matches(TRACK)) {
        scrollMemo = t.scrollLeft;
        updateBar(t);
      }
    },
    true // capture — scroll does not bubble
  );

  function restoreScroll() {
    var tracks = document.querySelectorAll(TRACK);
    for (var i = 0; i < tracks.length; i++) {
      if (scrollMemo && tracks[i].scrollLeft === 0 && tracks[i].scrollWidth > tracks[i].clientWidth) {
        tracks[i].scrollLeft = scrollMemo;
      }
    }
  }

  function watchCartContainers() {
    if (typeof MutationObserver !== 'function') return;
    var containers = document.querySelectorAll('.js-ajax-cart-content');
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].getAttribute('data-bju-watched') === '1') continue;
      containers[i].setAttribute('data-bju-watched', '1');
      new MutationObserver(function () {
        // the rail is a brand new element after every cart change
        restoreScroll();
        initRails();
        // catches removals and quantity changes made through the theme's own
        // controls, which never pass through this file
        syncUpsellAttribute();
      }).observe(containers[i], { childList: true });
    }
  }

  /* --- delegated events -------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var sw = closest(e.target, '[data-bju-swatch]');
    if (sw) {
      e.preventDefault();
      selectSwatch(sw);
      return;
    }
    var add = closest(e.target, '[data-bju-add]');
    if (add) {
      e.preventDefault();
      addToCart(add);
    }
  });

  document.addEventListener('keydown', function (e) {
    var sw = closest(e.target, '[data-bju-swatch]');
    if (!sw) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      moveSwatch(sw, 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveSwatch(sw, -1);
    }
  });

  function boot() {
    watchCartContainers();
    initRails();
    // reconcile after a page navigation (the cart may have changed elsewhere)
    syncUpsellAttribute();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();


/* ==========================================================================
   Cart extras — separate boxes, discount code, order note (16 Oct 2026)
   + gift note (23 Oct 2026)
   Markup: snippets/cart-form.liquid (.bj-extras, .bj-sum__code).

   The code and note fields open in ONE small panel pinned to the TOP of the
   screen. In the frozen footer the phone keyboard covered them (Charlie,
   16 Oct): the footer is fixed to the bottom and the keyboard opens over it.
   The side cart is a popover="auto" (browser top layer), so the panel is a
   popover too, placed INSIDE the side cart: that puts it above the cart, and
   taps inside it don't count as "outside" (which would close the cart). It
   sits outside .js-ajax-cart-content, so the theme's re-render never wipes it.

   - Discount code: Cart AJAX API `discount` parameter (/cart/update.js). The
     code then rides along to checkout AND to Shop Pay / Apple Pay / Google
     Pay / PayPal, whose sheets have no code box. A code the bag can't use is
     taken off again and the previous code (if any) put back.
   - Note: saved to the cart note (where the team reads box requests) and to
     the hidden name="note" field, so the Check Out button sends it too.
   - Gift note: saved as the cart ATTRIBUTE "Gift note" (/cart/update.js
     `attributes`), never the cart note, so a box request and a gift note can
     coexist. It lands on the order under Additional details and is printed by
     the Order Printer "Gift receipt" template. 200 characters, with a counter.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__bjCartExtrasInit) return;
  window.__bjCartExtrasInit = true;

  var GIFT_KEY = 'Gift note';
  var GIFT_MAX = 200;

  function closest(el, sel) {
    return el && el.closest ? el.closest(sel) : null;
  }
  function cartConfig() {
    try {
      return JSON.parse(document.getElementById('cart-config').innerHTML || '{}');
    } catch (e) {
      return {};
    }
  }
  function post(body) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error('cart ' + r.status);
      return r.json();
    });
  }
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }
  function redraw(cart) {
    try {
      Shopify.theme.ajaxCart.updateView(cartConfig(), cart);
    } catch (e) {
      window.location.reload();
    }
  }
  function el(tag, cls, attrs) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (attrs) for (var k in attrs) n.setAttribute(k, attrs[k]);
    return n;
  }
  // The .bj-extras block the shopper is looking at (side cart first).
  function currentExtras(from) {
    if (from && from.isConnected) return closest(from, '[data-bj-extras]');
    return document.querySelector('#slideout-ajax-cart [data-bj-extras], #modal-ajax-cart [data-bj-extras]') ||
      document.querySelector('[data-bj-extras]');
  }

  /* --- the panel ----------------------------------------------------------- */
  var sheet, card, titleEl, bodyEl, mode = null, opener = null, hideT = null;
  var canPop = typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';
  function isShown() {
    if (!sheet) return false;
    if (canPop) {
      try { return sheet.matches(':popover-open'); } catch (e) { return false; }
    }
    return !sheet.hidden;
  }
  function build() {
    if (sheet) return;
    sheet = el('div', 'bj-sheet', { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'bj-sheet-title' });
    if (canPop) {
      sheet.setAttribute('popover', 'auto');
      // Escape (or the browser) closed it: tidy up.
      sheet.addEventListener('toggle', function (e) {
        if (e.newState === 'closed') {
          sheet.classList.remove('is-open');
          bodyEl.innerHTML = '';
          mode = null;
        }
      });
    } else {
      sheet.hidden = true;
    }
    var scrim = el('div', 'bj-sheet__scrim', { 'data-bj-sheet-close': '' });
    card = el('div', 'bj-sheet__card', { tabindex: '-1' });
    var head = el('div', 'bj-sheet__head');
    titleEl = el('h2', 'bj-sheet__title', { id: 'bj-sheet-title' });
    var x = el('button', 'bj-sheet__x', { type: 'button', 'data-bj-sheet-close': '', 'aria-label': 'Close' });
    x.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    head.appendChild(titleEl);
    head.appendChild(x);
    bodyEl = el('div', 'bj-sheet__body');
    card.appendChild(head);
    card.appendChild(bodyEl);
    sheet.appendChild(scrim);
    sheet.appendChild(card);
  }

  function open(kind, from) {
    build();
    mode = kind;
    opener = from;
    clearTimeout(hideT);
    var ex = currentExtras(from);
    var hid = ex && ex.querySelector('[data-bj-note]');
    bodyEl.innerHTML = '';
    var focusEl;
    if (kind === 'disc') {
      titleEl.textContent = 'Discount code';
      var row = el('div', 'bj-sheet__row');
      var inp = el('input', 'bj-sheet__input', {
        type: 'text', 'aria-label': 'Discount code', placeholder: 'Enter your code',
        autocomplete: 'off', autocapitalize: 'characters', autocorrect: 'off', spellcheck: 'false', enterkeyhint: 'go',
        'data-bj-sheet-code': ''
      });
      var btn = el('button', 'bj-sheet__btn', { type: 'button', 'data-bj-sheet-apply': '' });
      btn.textContent = 'Apply';
      row.appendChild(inp);
      row.appendChild(btn);
      bodyEl.appendChild(row);
      var msg = el('p', 'bj-sheet__msg', { role: 'status', 'data-bj-sheet-msg': '' });
      msg.hidden = true;
      bodyEl.appendChild(msg);
      focusEl = inp;
    } else if (kind === 'gift') {
      // Gift note (23 Oct 2026). Prefilled from the .bj-extras data attribute,
      // which the Liquid writes from cart.attributes['Gift note'].
      var current = (ex && ex.getAttribute('data-gift-note')) || '';
      titleEl.textContent = 'Is this a gift?';
      var gp = el('p', 'bj-sheet__text');
      // Wording (3 Nov 2026, Charlie): make it clear the note is printed on the
      // gift receipt in the parcel, not on a separate card.
      gp.textContent = 'Sending it straight to them? Add a message and we’ll print it on a gift receipt with no prices, tucked inside the parcel. Just use their address at checkout.';
      bodyEl.appendChild(gp);
      var gn = el('p', 'bj-sheet__small');
      gn.textContent = 'Your message is printed on the gift receipt, not on a separate card.';
      bodyEl.appendChild(gn);
      var garea = el('textarea', 'bj-sheet__area', {
        rows: '4', maxlength: String(GIFT_MAX), 'aria-label': 'Your gift note', 'data-bj-sheet-gift': '',
        placeholder: 'For example: Happy birthday Mum! Have a lovely day. Lots of love, Annabel x'
      });
      garea.value = current;
      bodyEl.appendChild(garea);
      var hint = el('div', 'bj-sheet__hint');
      var hl = el('span');
      hl.textContent = 'We’ll print it exactly as you write it.';
      var hc = el('span', '', { 'data-bj-sheet-count': '', 'aria-live': 'polite' });
      hc.textContent = current.length + ' / ' + GIFT_MAX;
      hint.appendChild(hl);
      hint.appendChild(hc);
      bodyEl.appendChild(hint);
      var gsave = el('button', 'bj-sheet__save', { type: 'button', 'data-bj-sheet-save': '' });
      gsave.textContent = current ? 'Save gift note' : 'Add gift note';
      bodyEl.appendChild(gsave);
      focusEl = garea;
    } else {
      var isBoxes = kind === 'boxes';
      titleEl.textContent = isBoxes ? (ex && ex.getAttribute('data-box-q')) || 'Do you require separate boxes?' : 'Add a note';
      var p = el('p', 'bj-sheet__text');
      p.textContent = isBoxes
        ? (ex && ex.getAttribute('data-box-text')) || 'All orders come boxed together. If you require a separate box for any of your pieces (excluding stacks and duos), please leave a note below.'
        : 'Anything we should know? For example a bespoke size.';
      bodyEl.appendChild(p);
      var area = el('textarea', 'bj-sheet__area', {
        rows: '3', maxlength: '500', 'aria-label': isBoxes ? 'Your note about boxes' : 'Your note', 'data-bj-sheet-note': '',
        placeholder: isBoxes ? 'For example: two boxes please, one for the pearls.' : 'Type your note here'
      });
      area.value = hid ? hid.value : '';
      bodyEl.appendChild(area);
      var save = el('button', 'bj-sheet__save', { type: 'button', 'data-bj-sheet-save': '' });
      save.textContent = 'Save note';
      bodyEl.appendChild(save);
      focusEl = isBoxes ? null : area;
    }
    // Inside the open side cart (see top of this block), else the page.
    var host = (from && closest(from, '[popover]')) || document.body;
    if (sheet.parentNode !== host && !isShown()) host.appendChild(sheet);
    if (canPop) {
      if (!isShown()) {
        try { sheet.showPopover(); } catch (e) { sheet.removeAttribute('popover'); canPop = false; }
      }
    }
    if (!canPop) sheet.hidden = false;
    void card.offsetHeight;
    sheet.classList.add('is-open');
    // Focus inside the tap itself, or iOS won't raise the keyboard. The boxes
    // panel opens on the wording first; the keyboard comes up when they tap
    // the box (or they use the one-tap shortcut).
    if (focusEl) {
      try { focusEl.focus({ preventScroll: true }); } catch (e) { focusEl.focus(); }
    } else {
      try { card.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  function close() {
    if (!isShown()) return;
    var a = document.activeElement;
    if (a && sheet.contains(a) && a.blur) a.blur();
    sheet.classList.remove('is-open');
    hideT = setTimeout(function () {
      if (canPop) {
        try { sheet.hidePopover(); } catch (e) {}
      } else {
        sheet.hidden = true;
      }
      bodyEl.innerHTML = '';
    }, 200);
    mode = null;
    if (opener && opener.isConnected && opener.focus) {
      try { opener.focus({ preventScroll: true }); } catch (e) {}
    }
  }

  /* --- note ---------------------------------------------------------------- */
  function saveNote(btn) {
    var gift = sheet.querySelector('[data-bj-sheet-gift]');
    if (gift) return saveGift(btn, gift);
    var area = sheet.querySelector('[data-bj-sheet-note]');
    if (!area) return;
    var note = area.value.trim();
    var ex = currentExtras(opener);
    var hid = ex && ex.querySelector('[data-bj-note]');
    if (hid) hid.value = note;
    btn.disabled = true;
    btn.textContent = 'Saving…';
    post({ note: note })
      .then(function (cart) {
        close();
        redraw(cart);
      })
      .catch(function () {
        // The hidden field still carries it to checkout via Check Out.
        close();
      });
  }

  /* --- gift note (cart attribute) ------------------------------------------ */
  function saveGift(btn, area) {
    var note = area.value.trim().slice(0, GIFT_MAX);
    var ex = currentExtras(opener);
    var hid = ex && ex.querySelector('[data-bj-gift]');
    if (hid) hid.value = note;
    if (ex) ex.setAttribute('data-gift-note', note);
    btn.disabled = true;
    btn.textContent = 'Saving…';
    var attributes = {};
    attributes[GIFT_KEY] = note; // '' removes the attribute
    post({ attributes: attributes })
      .then(function (cart) {
        // A saved note already shows as the "Gift note added" line under the
        // links, so only a removal needs a message.
        pending = note ? null : { text: 'Gift note removed.', kind: '' };
        close();
        redraw(cart);
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = 'Try again';
      });
  }

  /* --- + / − tap feedback (3 Nov 2026, Charlie) ----------------------------
     A light grey flash for a moment when tapped, then back to clear. Phones
     don't reliably show :active, and the theme's hover grey used to stick
     until the bag redrew; CSS in bj-cart-upsell.css (.is-tapped). */
  document.addEventListener('pointerdown', function (e) {
    var b = e.target && e.target.closest && e.target.closest('.ajax-cart__qty-control--up, .ajax-cart__qty-control--down');
    if (!b) return;
    b.classList.add('is-tapped');
    setTimeout(function () { b.classList.remove('is-tapped'); }, 350);
  }, true);

  /* --- discount code ------------------------------------------------------- */
  var pending = null; // message shown in the bag after the theme redraws it

  function sheetMsg(text, kind) {
    var m = sheet && sheet.querySelector('[data-bj-sheet-msg]');
    if (!m) return;
    m.textContent = text || '';
    m.hidden = !text;
    m.className = 'bj-sheet__msg' + (kind ? ' is-' + kind : '');
  }
  function bagMsg(ex, text, kind) {
    var m = ex && ex.querySelector('[data-bj-disc-msg]');
    if (!m) return;
    m.textContent = text || '';
    m.hidden = !text;
    m.className = 'bj-disc__msg' + (kind ? ' is-' + kind : '');
  }
  function restorePending() {
    if (!pending) return;
    var all = document.querySelectorAll('[data-bj-extras]');
    if (!all.length) return;
    var p = pending;
    pending = null;
    for (var i = 0; i < all.length; i++) bagMsg(all[i], p.text, p.kind);
  }

  function applyCode() {
    var inp = sheet.querySelector('[data-bj-sheet-code]');
    var btn = sheet.querySelector('[data-bj-sheet-apply]');
    if (!inp || !btn || btn.disabled) return;
    var code = (inp.value || '').trim();
    if (!code) {
      sheetMsg('Please enter your code.', 'err');
      inp.focus();
      return;
    }
    btn.disabled = true;
    btn.textContent = 'Applying…';
    sheetMsg('', '');
    var done = function () {
      btn.disabled = false;
      btn.textContent = 'Apply';
    };
    getCart()
      .then(function (before) {
        var keep = (before.discount_codes || [])
          .filter(function (d) { return d.applicable; })
          .map(function (d) { return d.code; });
        return post({ discount: code }).then(function (cart) {
          var hit = (cart.discount_codes || []).filter(function (d) {
            return String(d.code).toLowerCase() === code.toLowerCase();
          })[0];
          if (hit && hit.applicable) {
            // No "CODE applied." line (Charlie, 3 Nov 2026): the Discount row
            // already shows the code and the saving.
            pending = null;
            close();
            redraw(cart);
            return;
          }
          return post({ discount: keep.join(',') }).then(function (back) {
            done();
            sheetMsg("Sorry, that code can't be used on this bag. Please check it, and any conditions such as a minimum spend.", 'err');
            redraw(back);
          });
        });
      })
      .catch(function () {
        done();
        sheetMsg('Sorry, something went wrong. Please try again, or add your code at checkout.', 'err');
      });
  }

  function removeCode(btn) {
    var code = String(btn.getAttribute('data-code') || '').toLowerCase();
    btn.disabled = true;
    getCart()
      .then(function (cart) {
        var keep = (cart.discount_codes || [])
          .map(function (d) { return d.code; })
          .filter(function (c) { return String(c).toLowerCase() !== code; });
        return post({ discount: keep.join(',') });
      })
      .then(function (cart) {
        pending = null;
        redraw(cart);
      })
      .catch(function () {
        btn.disabled = false;
      });
  }

  /* --- delegated events ---------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var t = closest(e.target, '[data-bj-open]');
    if (t) {
      e.preventDefault();
      bagMsg(closest(t, '[data-bj-extras]'), '', '');
      open(t.getAttribute('data-bj-open'), t);
      return;
    }
    if (closest(e.target, '[data-bj-sheet-close]')) {
      e.preventDefault();
      close();
      return;
    }
    if (closest(e.target, '[data-bj-sheet-apply]')) {
      e.preventDefault();
      applyCode();
      return;
    }
    var sv = closest(e.target, '[data-bj-sheet-save]');
    if (sv) {
      e.preventDefault();
      saveNote(sv);
      return;
    }
    var rm = closest(e.target, '[data-bj-disc-remove]');
    if (rm) {
      e.preventDefault();
      removeCode(rm);
    }
  });

  // Live "48 / 200" counter under the gift note box.
  document.addEventListener('input', function (e) {
    var a = closest(e.target, '[data-bj-sheet-gift]');
    if (!a || !sheet) return;
    var c = sheet.querySelector('[data-bj-sheet-count]');
    if (c) c.textContent = a.value.length + ' / ' + GIFT_MAX;
  });

  document.addEventListener('keydown', function (e) {
    if (!isShown()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Enter' && closest(e.target, '[data-bj-sheet-code]')) {
      e.preventDefault();
      applyCode();
    }
  });

  function watch() {
    if (typeof MutationObserver !== 'function') return;
    var containers = document.querySelectorAll('.js-ajax-cart-content');
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].getAttribute('data-bj-extras-watched') === '1') continue;
      containers[i].setAttribute('data-bj-extras-watched', '1');
      new MutationObserver(restorePending).observe(containers[i], { childList: true });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();

/* ==========================================================================
   Judge.me Certified badge under Check Out (28 Oct 2026)
   snippets/cart-form.liquid renders Judge.me's badge "lazy" (data-bj-entry-
   point, which Judge.me's loader ignores): the bag is on every page, hidden,
   and a normal badge there would make every page load Judge.me's badge files.
   When the bag is opened, and after every bag re-render while it shows, the
   badge is handed to Judge.me's own badge initialiser. Pages that already show
   a badge (home, product pages) have it ready; elsewhere Judge.me's badge
   files are loaded the way its loader does it (manifest CSS + module). If
   anything fails the old "Secure checkout · rating" line shows (.is-fallback).

   Tapping it opens Judge.me's pop-up as usual. Judge.me adds the pop-up to
   <body>, which is underneath the bag (the bag is in the browser's top layer),
   so it is moved into a popover layer inside the bag: that stacks above the
   bag, and taps in it count as inside the bag, so the bag stays open. Judge.me
   removes the pop-up itself when it closes; if the bag closes first, the pop-up
   goes back to <body>.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__bjCartBadgeInit) return;
  window.__bjCartBadgeInit = true;

  var WIDGET = '[data-bj-cartjm] .jdgm-trust-badge-widget[data-bj-entry-point]';
  var DRAWERS = ['slideout-ajax-cart', 'modal-ajax-cart'];
  var ENTRY_KEY = 'trust-badge/main.js';
  var GIVE_UP_MS = 8000;
  var loading = null;

  function initializer() {
    return window.__trustBadgeInitializer;
  }

  function addCss(base, manifest, key, seen) {
    var entry = manifest[key];
    if (!entry || seen[key]) return;
    seen[key] = true;
    (entry.css || []).forEach(function (file) {
      var href = base + file;
      if (document.querySelector('link[href="' + href + '"]')) return;
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    });
    (entry.imports || []).forEach(function (k) {
      addCss(base, manifest, k, seen);
    });
  }

  function loadJudgeme() {
    if (loading) return loading;
    loading = new Promise(function (resolve, reject) {
      var base = window.jdgm && window.jdgm.CDN_BASE_URL;
      if (!base || typeof fetch !== 'function') return reject(new Error('Judge.me not on page'));
      fetch(base + 'manifest.json?v=' + Date.now())
        .then(function (r) { return r.json(); })
        .then(function (manifest) { addCss(base, manifest, ENTRY_KEY, {}); })
        .catch(function () {})
        .then(function () {
          var src = base + 'trust_badge.js';
          if (!document.querySelector('script[src="' + src + '"]')) {
            var script = document.createElement('script');
            script.type = 'module';
            script.src = src;
            document.head.appendChild(script);
          }
          var waited = 0;
          (function wait() {
            if (initializer()) return resolve(initializer());
            waited += 100;
            if (waited >= GIVE_UP_MS) return reject(new Error('Judge.me badge did not load'));
            setTimeout(wait, 100);
          })();
        });
    });
    return loading;
  }

  function drawn(el) {
    return !!el.querySelector('.jdgm-trust-badge');
  }

  function useFallback(el) {
    if (drawn(el)) return;
    var box = el.closest('[data-bj-cartjm]');
    if (box) box.classList.add('is-fallback');
  }

  function mount() {
    var els = document.querySelectorAll(WIDGET);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      // only bags that are actually showing (an open drawer, or /cart)
      if (el.getAttribute('data-bj-jm') === '1' || !el.getClientRects().length) continue;
      el.setAttribute('data-bj-jm', '1');
      el.setAttribute('data-entry-point', el.getAttribute('data-bj-entry-point'));
      (function (el) {
        var ready = initializer() ? Promise.resolve(initializer()) : loadJudgeme();
        ready
          .then(function (init) {
            // a freshly loaded initialiser draws every badge it finds by itself
            if (!el.hasAttribute('data-mounted') && !drawn(el)) return init.initializeWidget(el);
          })
          .catch(function () { useFallback(el); });
        setTimeout(function () { useFallback(el); }, GIVE_UP_MS + 2000);
      })(el);
    }
  }

  /* --- Judge.me's pop-up above the bag ------------------------------------ */
  var layer = null;

  function modalInLayer() {
    return layer && layer.querySelector('.jdgm-modal');
  }

  function dropLayer() {
    var modal = modalInLayer();
    if (modal) document.body.appendChild(modal);
    if (layer && layer.matches(':popover-open')) layer.hidePopover();
  }

  function makeLayer() {
    layer = document.createElement('div');
    layer.className = 'bj-jmlayer';
    layer.setAttribute('popover', 'manual');
    // Show the layer whenever a Judge.me pop-up is in it, hide it when Judge.me
    // removes the pop-up (closed). After the first time, Judge.me opens the
    // pop-up straight into the layer: it reuses the spot where it last was.
    new MutationObserver(function () {
      var open = layer.matches(':popover-open');
      if (modalInLayer() && !open) {
        try {
          layer.showPopover();
        } catch (err) {
          document.body.appendChild(modalInLayer());
        }
      } else if (!modalInLayer() && open) {
        layer.hidePopover();
      }
    }).observe(layer, { childList: true });
  }

  function liftNextModal(drawer) {
    if (!layer) makeLayer();
    if (layer.parentNode !== drawer) drawer.appendChild(layer);
    if (drawer.getAttribute('data-bj-jm-layer') !== '1') {
      drawer.setAttribute('data-bj-jm-layer', '1');
      drawer.addEventListener('toggle', function (e) {
        if (e.newState === 'closed') dropLayer();
      });
    }
    // first time (or after the bag closed with it open) the pop-up lands on
    // <body>: move it into the layer before the browser paints
    var watcher = new MutationObserver(function () {
      var modal = document.querySelector('body > .jdgm-modal');
      if (!modal) return;
      watcher.disconnect();
      layer.appendChild(modal);
    });
    watcher.observe(document.body, { childList: true });
    setTimeout(function () { watcher.disconnect(); }, 3000);
  }

  // capture: set the watcher up before Judge.me's own click handler runs
  document.addEventListener('click', function (e) {
    var badge = e.target.closest && e.target.closest('[data-bj-cartjm] .jdgm-trust-badge');
    if (!badge) return;
    var drawer = badge.closest('[popover]');
    // on /cart there is no drawer and Judge.me's pop-up shows normally
    if (!drawer || typeof drawer.showPopover !== 'function') return;
    liftNextModal(drawer);
  }, true);

  function watch() {
    mount();
    if (typeof MutationObserver !== 'function') return;
    var containers = document.querySelectorAll('.js-ajax-cart-content');
    for (var i = 0; i < containers.length; i++) {
      if (containers[i].getAttribute('data-bj-jm-watched') === '1') continue;
      containers[i].setAttribute('data-bj-jm-watched', '1');
      new MutationObserver(mount).observe(containers[i], { childList: true });
    }
    DRAWERS.forEach(function (id) {
      var drawer = document.getElementById(id);
      if (!drawer || drawer.getAttribute('data-bj-jm-watched') === '1') return;
      drawer.setAttribute('data-bj-jm-watched', '1');
      // popover drawers fire "toggle"; class/style-driven ones change attributes
      drawer.addEventListener('toggle', function () { requestAnimationFrame(mount); });
      new MutationObserver(function () { requestAnimationFrame(mount); })
        .observe(drawer, { attributes: true, attributeFilter: ['class', 'style', 'open', 'aria-hidden'] });
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
  else watch();
})();
